/**
 * Workflow declaration / withdrawal dialogs (v2.5 SS-02 / PR B2 round 1).
 *
 * Declaring a workflow is an ADMIN decision with visible consequences — the
 * dialog shows the exact component set (with required flags, fetched from
 * GET /workflow/default) BEFORE anything is written, and the declaration has
 * a persistent audited undo: Withdraw (DELETE /workflow) returns the journey
 * to workflow-unknown with the withdrawn set preserved in the audit trail.
 *
 * Same accessibility contract as the lifecycle confirmation dialog:
 * accessible name, initial focus, focus trap, Escape to cancel (focus
 * restoration is the caller's job via its trigger ref).
 */

import React, { useEffect, useRef } from 'react';

export interface WorkflowComponentPreview {
  component_id: string;
  label: string;
  required: boolean;
}

interface WorkflowDeclarationDialogProps {
  mode: 'declare' | 'withdraw';
  journeyName: string;
  /** The component set a declaration would apply (declare mode). */
  components: WorkflowComponentPreview[];
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export const WorkflowDeclarationDialog: React.FC<WorkflowDeclarationDialogProps> = ({
  mode,
  journeyName,
  components,
  busy,
  error,
  onConfirm,
  onCancel,
}) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  // Initial focus: the NAMED HEADING (G0) — the dialog's purpose is
  // announced before any control.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key === 'Tab') {
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea'
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === headingRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const titleId = 'jsp-workflow-dialog-title';

  return (
    <div className="jsp-reason-backdrop" onKeyDown={onKeyDown}>
      <div
        className="jsp-reason-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        {mode === 'declare' ? (
          <>
            <h3 id={titleId} tabIndex={-1} ref={headingRef}>
              Declare the workflow for &quot;{journeyName}&quot;?
            </h3>
            <p>
              Declaring sets which components count toward this journey&apos;s completeness.
              Exactly this reviewed set is submitted — the journey immediately gains
              &quot;X of Y&quot; tallies against it:
            </p>
            <ul className="jsp-dialog-consequences">
              {components.map((c) => (
                <li key={c.component_id}>
                  {c.label}
                  {c.required ? ' — required' : ' — optional'}
                </li>
              ))}
            </ul>
            <p>
              Recorded in the audit log as &quot;workflow_declared&quot; (this component list, with
              required flags) with your name, role, and timestamp. You can withdraw the
              declaration later (also audited) — the journey then returns to
              &quot;Completeness unknown&quot;.
            </p>
          </>
        ) : (
          <>
            <h3 id={titleId} tabIndex={-1} ref={headingRef}>
              Withdraw the workflow declaration for &quot;{journeyName}&quot;?
            </h3>
            <ul className="jsp-dialog-consequences">
              <li>The journey returns to &quot;Completeness unknown&quot; — no numeric tallies anywhere.</li>
              <li>No results or history are deleted; the withdrawn component set is preserved in the audit trail.</li>
              <li>
                Recorded in the audit log as &quot;workflow_declared&quot; (withdrawn: true, with the
                previous component set) with your name, role, and timestamp.
              </li>
            </ul>
          </>
        )}

        {error && (
          <div className="jsp-action-error" role="alert">
            {error}
          </div>
        )}

        <div className="jsp-reason-actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={onConfirm} disabled={busy}>
            {busy ? 'Applying…' : mode === 'declare' ? 'Declare workflow' : 'Withdraw declaration'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowDeclarationDialog;
